import { supabase } from './supabase';
import type { Category } from './database.client.types';

export { SOURCES, getPickerSources, type SourceId } from './sourceRegistry';

// Cache for categories to avoid repeated database calls
let categoriesCache: Category[] | null = null;
let cacheTimestamp: number = 0;
const pickerCache = new Map<number, { categories: Category[]; timestamp: number }>();
const CACHE_DURATION_MS = 5 * 60 * 60 * 1000; // 5 hours

/**
 * Helper function to check if a category has children
 * @param code - The category code to check
 * @param categories - Array of all categories
 * @returns boolean - True if the category has children
 */
const hasChildren = (code: string, categories: Category[]): boolean => {
    return categories.some(cat => cat.parent_code === code);
};

/**
 * Fetches all categories from the database with caching
 * @returns Promise<Category[]> - Array of all categories
 */
async function getAllCategories(): Promise<Category[]> {
    // Return cached data if still valid
    const now = Date.now();
    if (categoriesCache && (now - cacheTimestamp) < CACHE_DURATION_MS) {
        return categoriesCache;
    }

    // Fetch from database
    const { data, error } = await supabase
        .from('category')
        .select('*')
        .order('code');

    if (error) {
        console.error('Error fetching categories from database:', error);
        throw new Error('Failed to fetch categories from database');
    }

    // Update cache
    categoriesCache = data || [];
    cacheTimestamp = now;

    return categoriesCache;
}

/**
 * Gets categories suitable for the category picker
 * Includes all category-level items plus archive-level items that have no children
 * @param sourceId - Optional source ID to filter by (1: arXiv, 2: medRxiv, 3: bioRxiv)
 * @returns Promise<Category[]> - Array of selectable categories
 */
export async function getCategoriesForPicker(sourceId?: number): Promise<Category[]> {
    if (sourceId === undefined) {
        const allCategories = await getAllCategories();
        return allCategories.filter(cat =>
            cat.level === 'category' ||
            (cat.level === 'archive' && !hasChildren(cat.code, allCategories))
        );
    }

    const now = Date.now();
    const cached = pickerCache.get(sourceId);
    if (cached && now - cached.timestamp < CACHE_DURATION_MS) {
        return cached.categories;
    }

    // Query only the selected source. This prevents restricted source
    // taxonomies from being downloaded before access has been established.
    const { data, error } = await supabase
        .from('category')
        .select('*')
        .eq('source_id', sourceId)
        .order('code');
    if (error) {
        console.error('Error fetching categories for source:', sourceId, error);
        throw new Error('Failed to fetch categories from database');
    }
    const sourceCategories = data || [];

    // Filter by level (category or archive without children)
    const selectable = sourceCategories.filter(cat =>
        cat.level === 'category' ||
        (cat.level === 'archive' && !hasChildren(cat.code, sourceCategories))
    );
    pickerCache.set(sourceId, { categories: selectable, timestamp: now });
    return selectable;
}

/**
 * Fetches all categories from the database
 * @returns Promise<Category[]> - Array of all categories
 */
export async function fetchCategories(): Promise<Category[]> {
    return getAllCategories();
}
